/**
 * The Catalog: saved charts, yours and other people's.
 *
 * A dense list rather than a grid of cards, for the same reason the dashboard
 * browse screen is one - there are no previews to show yet, and a card without a
 * picture is a row with wasted space around it. Each row's second line is built
 * from the stored parameters (`chart-summary.ts`), so it says what the chart
 * actually draws instead of just when it was touched.
 *
 * Three tabs, one request: `scope=all` comes back with `is_owner`,
 * `shared_directly` and `is_public` on every row, so "My charts", "Shared with
 * me" and "Public" are cut from the same list and their counts can never
 * disagree with the grid under them.
 *
 * Under the tabs, inside the folder, a filter row narrows the tab by the facts a
 * chart carries - who made it, what kind of plot, template or pinned, which
 * ship, which tags. It offers only facets that would actually split this tab.
 *
 * The tabs are folder tabs. The one you are in is drawn as a tab with its top
 * right corner cut, sharing one outline with the card area below, so it reads
 * as the folder you have open rather than as a highlighted word. Switching
 * slides that tab outline across to the other label (`placeFolderTab`).
 *
 * What a row offers depends on `can_edit`, which the server computes per row.
 * Everyone can open, copy a link and duplicate; only an owner or someone holding
 * a grant over that chart can rename, re-save or delete. Hiding a control the
 * server would refuse is a courtesy - the server still refuses it.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
// Keyed by chart id: without it Lit reuses cards by position, and a card would
// keep the previous chart's loaded preview when the list is filtered or sorted.
import { repeat } from 'lit/directives/repeat.js';
import { customElement, state } from 'lit/decorators.js';
import type { CatalogChart } from '../../api/types.ts';
import { LightElement } from '../../lit-base.ts';
import '../../ui/plain-select.ts';
import { StoreController } from '../../state/controller.ts';
import { activeSection, catalogAvailable, shipMeta } from '../../state/store.ts';
import { shortenCurrentView } from '../../state/share-link.ts';
import { showToast } from '../../ui/toast.ts';
import './catalog-card.ts';
import './catalog-filter.ts';
import '../dashboard/chart-builder.ts';
import {
  catalogCharts,
  catalogError,
  catalogFilters,
  catalogLoaded,
  catalogLoading,
  catalogQuery,
  catalogScope,
  catalogSort,
  clearFilters,
  duplicateChart,
  effectiveSort,
  facetsFor,
  filterOptions,
  hasFilters,
  loadCatalog,
  openChart,
  openShareDialog,
  removeChart,
  scopedCharts,
  setCatalogScope,
  setCatalogSort,
  setFilter,
  setPublic,
  sortsFor,
  updateChartDetails,
  visibleCharts,
  type CatalogScope,
  type CatalogSort,
  type FilterFacet,
} from './catalog-state.ts';

/** What each facet is called in the filter row. */
const FACET_LABELS: Record<FilterFacet, string> = {
  people: 'People',
  types: 'Type',
  kinds: 'Kind',
  ships: 'Ship',
  tags: 'Tags',
};

/**
 * Refresh glyph: a clockwise arrow in the toolbar's own idiom - stroke SVG on
 * `currentColor`, 24 viewBox, 1.8 stroke, like the dashboard Save glyph - so
 * it follows the button's colour in both themes.
 */
const REFRESH_GLYPH = svg`<svg
  class="catalog-refresh-glyph"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M20.5 12a8.5 8.5 0 1 1-2.5-6"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
  />
  <path
    d="M20.5 3.5V8h-4.5"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** One full turn of the refresh glyph - see `refresh()`. */
const REFRESH_TURN_MS = 600;

@customElement('catalog-page')
export class CatalogPage extends LightElement {
  private store = new StoreController(this, [
    catalogCharts,
    catalogScope,
    catalogQuery,
    catalogSort,
    catalogFilters,
    catalogLoading,
    catalogLoaded,
    catalogError,
    catalogAvailable,
    activeSection,
    // Ship names load with the app, which can finish after this list has drawn.
    // Without this the cards would show `A-BL` until something else re-rendered
    // them, which is exactly what happened.
    shipMeta,
  ]);

  /** Which card just had its link copied, for the momentary "Copied" label. */
  @state() private copiedId: string | null = null;
  /** The saved chart currently borrowing Explore's editor and preview. */
  @state() private editingChart: CatalogChart | null = null;
  /** True while the refresh glyph is turning. */
  @state() private refreshing = false;

  /** The scope the folder tab was last drawn at - a change is what slides it. */
  private folderTabScope: CatalogScope | null = null;
  /**
   * Re-places the folder tab when a label changes width without a render - the
   * web font arriving, or the section being shown after it was measured hidden.
   */
  private tabResize = new ResizeObserver(() => this.placeFolderTab(false));

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.tabResize.disconnect();
  }

  override firstUpdated(): void {
    for (const tab of this.querySelectorAll('.catalog-tab')) this.tabResize.observe(tab);
  }

  /**
   * Load when the section is on screen and a database is known to exist.
   *
   * Not in `connectedCallback`: this element mounts during boot, before
   * `/api/info` has said whether there is a catalog at all, and a load fired
   * then would find availability false and stop. Driving it from `updated()`
   * means the same check runs again the moment either fact changes, and the
   * guard inside `loadCatalog` keeps it to one request.
   */
  override updated(): void {
    const scope = catalogScope.value;
    this.placeFolderTab(this.folderTabScope !== null && this.folderTabScope !== scope);
    if (activeSection.value !== 'catalog') return;
    if (!catalogAvailable.value || catalogLoaded.value || catalogLoading.value) return;
    void loadCatalog();
  }

  /**
   * Put the folder tab outline behind the active label.
   *
   * Measured rather than laid out in CSS because the two labels differ in width
   * and the outline has to travel between them: one element whose `left` and
   * `width` transition is what makes it slide instead of blink. It is only
   * animated for a change of tab; a resize or a first draw snaps into place.
   * Skipped while hidden - a zero-width measure would make the next real one
   * grow out of nothing.
   */
  private placeFolderTab(animate: boolean): void {
    const tab = this.querySelector<HTMLElement>('.catalog-tab.active');
    const outline = this.querySelector<HTMLElement>('.catalog-folder-tab');
    if (!tab || !outline || !tab.offsetWidth) return;
    this.folderTabScope = catalogScope.value;
    const left = `${tab.offsetLeft}px`;
    const width = `${tab.offsetWidth}px`;
    // Every render lands here; one that moves nothing must not cut a slide short.
    if (outline.style.left === left && outline.style.width === width) return;
    outline.classList.toggle('is-sliding', animate);
    outline.style.left = left;
    outline.style.width = width;
    outline.hidden = false;
  }

  /**
   * Reload the list, turning the glyph at least once.
   *
   * The request is usually back in a few milliseconds, and a glyph that twitches
   * for one frame reads as nothing having happened. One whole turn says "done"
   * whatever the network did; a slow load keeps it turning until it lands.
   */
  private async refresh(): Promise<void> {
    if (this.refreshing) return;
    this.refreshing = true;
    await Promise.all([
      loadCatalog(),
      new Promise((resolve) => setTimeout(resolve, REFRESH_TURN_MS)),
    ]);
    this.refreshing = false;
  }

  /**
   * Copy a link to one chart.
   *
   * `/c/<id>` tracks the owner's edits, which is what someone linking a *saved*
   * chart means. A frozen snapshot of the current view is what `/s/<id>` is for,
   * and that comes from the chart-header Share action instead.
   */
  private async onCopyLink(chart: CatalogChart): Promise<void> {
    const url = `${window.location.origin}/c/${chart.id}`;
    try {
      await navigator.clipboard.writeText(url);
      this.copiedId = chart.id;
      setTimeout(() => {
        if (this.copiedId === chart.id) {
          this.copiedId = null;
          this.requestUpdate();
        }
      }, 1400);
    } catch {
      showToast('error', 'Copy failed - the link is ' + url);
    }
  }

  private renderTab(scope: CatalogScope, label: string): TemplateResult {
    const count = scopedCharts(scope).length;
    const active = catalogScope.value === scope;
    // `context-tab` is the Plot / Export / Model Viewer tab style - its type and
    // colours, reused rather than reproduced. Only the underline is swapped for
    // the folder outline (catalog.css), which is what these tabs open onto.
    return html`<button
      class="context-tab catalog-tab ${active ? 'active' : ''}"
      role="tab"
      aria-selected=${active ? 'true' : 'false'}
      @click=${() => setCatalogScope(scope)}
    >
      ${label}
      <span class="catalog-tab-count">${count}</span>
    </button>`;
  }

  private renderCard(chart: CatalogChart): TemplateResult {
    return html`<catalog-card
      data-chart-id=${chart.id}
      .chart=${chart}
      .copied=${this.copiedId === chart.id}
      .onOpen=${(c: CatalogChart) => void openChart(c)}
      .onEdit=${(c: CatalogChart) => { this.editingChart = c; }}
      .onShare=${(c: CatalogChart) => void openShareDialog(c.id)}
      .onDuplicate=${(c: CatalogChart) => void duplicateChart(c)}
      .onDelete=${(c: CatalogChart) => void removeChart(c.id)}
      .onRename=${(c: CatalogChart, title: string) => void updateChartDetails(c.id, { title })}
      .onSetPublic=${(c: CatalogChart, isPublic: boolean) => void setPublic(c.id, isPublic)}
      .onCopyLink=${(c: CatalogChart) => void this.onCopyLink(c)}
    ></catalog-card>`;
  }

  /**
   * The filter row. A facet is offered only when it would split this tab -
   * two or more values - or when it is already ticked, so it can be cleared.
   * With nothing worth offering, the row is not drawn at all.
   */
  private renderFilters(shown: number): TemplateResult | typeof nothing {
    const scope = catalogScope.value;
    const filters = catalogFilters.value;
    const facets = facetsFor(scope)
      .map((facet) => ({ facet, options: filterOptions(scope, facet) }))
      .filter(({ facet, options }) => options.length > 1 || filters[facet].length > 0);
    if (!facets.length) return nothing;
    const total = scopedCharts(scope).length;
    const narrowed = hasFilters(scope) || catalogQuery.value.trim() !== '';
    return html`<div class="catalog-filters" role="group" aria-label="Filter charts">
      <svg class="catalog-filters-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 5h16l-6.2 7.4V19l-3.6-1.8v-4.8L4 5z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      </svg>
      ${facets.map(
        ({ facet, options }) => html`<catalog-filter
          .label=${FACET_LABELS[facet]}
          .options=${options}
          .selected=${filters[facet]}
          .change=${(values: string[]) => setFilter(facet, values)}
        ></catalog-filter>`,
      )}
      ${hasFilters(scope)
        ? html`<button class="catalog-filters-clear" @click=${() => clearFilters()}>Clear all</button>`
        : nothing}
      <span class="catalog-filters-count">
        ${narrowed ? `${shown} of ${total}` : `${total} ${total === 1 ? 'chart' : 'charts'}`}
      </span>
    </div>`;
  }

  /** Every state the list can be in that is not "here are the charts". */
  private renderEmpty(): TemplateResult {
    if (!catalogAvailable.value) {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">The catalog needs the metadata database</p>
        <p>
          This deployment is running without one, so charts cannot be saved or shared. Everything
          in Explore works exactly as it always has.
        </p>
      </div>`;
    }
    if (catalogError.value) {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">The catalog could not be loaded</p>
        <p>${catalogError.value}</p>
        <button class="catalog-cta" @click=${() => void loadCatalog()}>Try again</button>
      </div>`;
    }
    if (catalogLoading.value && !catalogLoaded.value) {
      return html`<div class="catalog-empty"><p>Loading…</p></div>`;
    }
    if (catalogQuery.value.trim() || hasFilters()) {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">
          ${catalogQuery.value.trim()
            ? html`Nothing matches “${catalogQuery.value}”`
            : 'No charts match these filters'}
        </p>
        <button
          class="catalog-cta"
          @click=${() => {
            catalogQuery.set('');
            clearFilters();
          }}
        >
          Clear search and filters
        </button>
      </div>`;
    }
    if (catalogScope.value === 'public') {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">Nothing public yet</p>
        <p>
          Charts anyone signed in can find, open and duplicate. To add one of yours, right-click it
          and choose <strong>Make public</strong>, or turn on <strong>Public</strong> when sharing.
        </p>
      </div>`;
    }
    if (catalogScope.value === 'mine') {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">Nothing saved yet</p>
        <p>
          Build a chart in Explore, then click <strong>Save</strong> in the chart header to keep it
          here.
        </p>
      </div>`;
    }
    return html`<div class="catalog-empty">
      <p class="catalog-empty-title">Nothing shared with you yet</p>
      <p>
        Charts are private to whoever saved them until they share one. When somebody does, it
        appears here.
      </p>
    </div>`;
  }

  override render(): TemplateResult {
    const charts = visibleCharts();
    const scope = catalogScope.value;
    return html`
      <section class="catalog" aria-label="Chart catalog">
        <header class="catalog-head">
          <div class="catalog-tabs" role="tablist">
            <!-- Drawn behind the labels and placed by placeFolderTab. Hidden until
                 it has been measured once, so it never shows at the wrong tab. -->
            <span class="catalog-folder-tab" aria-hidden="true" hidden></span>
            ${this.renderTab('mine', 'My charts')} ${this.renderTab('shared', 'Shared with me')}
            ${this.renderTab('public', 'Public')}
          </div>
          <div class="catalog-tools">
            <input
              class="catalog-search"
              type="search"
              placeholder="Search charts, columns, people…"
              .value=${catalogQuery.value}
              @input=${(e: Event) => catalogQuery.set((e.target as HTMLInputElement).value)}
            />
            <div class="catalog-sort">
              <span class="catalog-sort-label">Sort</span>
              <plain-select
                alignRight
                .options=${sortsFor(scope)}
                .value=${effectiveSort(scope)}
                .select=${(value: string) => setCatalogSort(value as CatalogSort)}
              ></plain-select>
            </div>
            <button
              class="catalog-icon-btn ${this.refreshing ? 'is-turning' : ''}"
              title="Refresh - fetch the latest charts"
              aria-label="Refresh"
              ?disabled=${catalogLoading.value && !this.refreshing}
              @click=${() => void this.refresh()}
            >
              ${REFRESH_GLYPH}
            </button>
          </div>
        </header>
        <div class="catalog-folder">
          ${this.renderFilters(charts.length)}
          ${charts.length
            ? html`<div class="catalog-grid">
                ${repeat(charts, (c) => c.id, (c) => this.renderCard(c))}
              </div>`
            : this.renderEmpty()}
        </div>
      </section>
      ${this.editingChart
        ? html`<chart-builder
            .catalogChart=${this.editingChart}
            @builder-close=${() => { this.editingChart = null; }}
          ></chart-builder>`
        : nothing}
      <chart-share-dialog></chart-share-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'catalog-page': CatalogPage;
  }
}
